import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFlooScopeOptions } from '@/hooks/useFlooScopeOptions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Building2, Map as MapIcon, Layers, Store, ArrowLeft, Network, Info,
} from 'lucide-react';
import { format } from 'date-fns';
import { clock } from '@/utils/clock';
import { cn } from '@/lib/utils';

// Floo Network Phase 7 — District Dashboard.
//
// Structural + lightweight-operational view of a single district. Same
// pattern as RegionDashboard: two-layer authorization (route guard +
// page-level check against `useFlooScopeOptions`), and per-shop metrics
// reused from `get_org_dashboard_metrics` filtered to district shops.

type DistrictRow = {
  id: string;
  name: string;
  organization_id: string;
  region_id: string | null;
  archived_at: string | null;
};

type RegionRow = {
  id: string;
  name: string;
};

type PerShop = {
  shop_id: string;
  shop_name: string;
  address: string | null;
  active_tickets: number;
  completed_in_range: number;
  booked_minutes: number;
  waiter_count: number;
  total_techs: number;
  total_capacity: number;
};

type DashboardMetrics = {
  total_shops: number;
  active_tickets: number;
  completed_in_range: number;
  vehicles_in_shop: number;
  estimated_revenue: number | null;
  total_booked_minutes: number;
  total_capacity_minutes: number;
  per_shop: PerShop[];
};

const DistrictDashboard = () => {
  const navigate = useNavigate();
  const { districtId } = useParams<{ districtId: string }>();
  const { user, userShops, isShopDataLoading } = useAuth();
  const {
    boundary,
    isLoading: scopeLoading,
    error: scopeError,
  } = useFlooScopeOptions();

  // Stabilization fix: boundary-based access (see RegionDashboard for
  // the rationale — the options list filters archived rows, so an
  // options-based check unreachable the archived-state UI for an
  // authorized user). Finalized after `district` resolves below.

  // ── District + parent metadata ───────────────────────────────────────
  const districtQuery = useQuery({
    queryKey: ['floo-district-dashboard', 'district', districtId],
    enabled: !!districtId,
    queryFn: async (): Promise<DistrictRow | null> => {
      const { data, error } = await supabase
        .from('districts')
        .select('id,name,organization_id,region_id,archived_at')
        .eq('id', districtId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as DistrictRow | null;
    },
  });

  const orgQuery = useQuery({
    queryKey: ['floo-district-dashboard', 'org', districtQuery.data?.organization_id ?? null],
    enabled: !!districtQuery.data?.organization_id,
    queryFn: async (): Promise<{ id: string; name: string } | null> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id,name')
        .eq('id', districtQuery.data!.organization_id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as { id: string; name: string } | null;
    },
  });

  const regionQuery = useQuery({
    queryKey: ['floo-district-dashboard', 'region', districtQuery.data?.region_id ?? null],
    enabled: !!districtQuery.data?.region_id,
    queryFn: async (): Promise<RegionRow | null> => {
      const { data, error } = await supabase
        .from('regions')
        .select('id,name')
        .eq('id', districtQuery.data!.region_id!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as RegionRow | null;
    },
  });

  // The parent region may not be in the user's allowed scopes (e.g.,
  // a DISTRICT_MANAGER has district scope only, not the parent region).
  // Boundary-based: matches the RegionDashboard access rule so an
  // archived parent region is still openable by org-level admins.
  const canOpenParentRegion = useMemo(() => {
    const rid = districtQuery.data?.region_id;
    const orgId = districtQuery.data?.organization_id;
    if (!rid || !orgId || !boundary) return false;
    if (boundary.type === 'organization' && boundary.id === orgId) return true;
    if (boundary.type === 'region' && boundary.id === rid) return true;
    return false;
  }, [boundary, districtQuery.data?.region_id, districtQuery.data?.organization_id]);

  // ── Metrics ──────────────────────────────────────────────────────────
  const metricsQuery = useQuery({
    queryKey: ['floo-district-dashboard', 'metrics', user?.id, todayString()],
    enabled: !!user?.id,
    queryFn: async (): Promise<DashboardMetrics | null> => {
      const today = todayString();
      const { data, error } = await supabase.rpc('get_org_dashboard_metrics', {
        _user_id: user!.id,
        _start_date: today,
        _end_date: today,
      });
      if (error) throw error;
      return (data as unknown as DashboardMetrics) ?? null;
    },
  });

  // ── Derived data ─────────────────────────────────────────────────────
  const district = districtQuery.data;
  const region = regionQuery.data;
  const org = orgQuery.data;

  // Boundary-based access. OWNER/ORG_ADMIN of the org, REGION_MANAGER
  // of the parent region, and DISTRICT_MANAGER of THIS district are
  // all granted access. Anything else (sibling-district managers,
  // shop-scoped MANAGER, operational roles) is denied.
  const hasAccess = useMemo(() => {
    if (!boundary || !district) return false;
    if (boundary.type === 'organization' && boundary.id === district.organization_id) return true;
    if (boundary.type === 'region' && district.region_id && boundary.id === district.region_id) return true;
    if (boundary.type === 'district' && boundary.id === district.id) return true;
    return false;
  }, [boundary, district]);

  const districtShops = useMemo(
    () => userShops.filter((s) => s.district_id === districtId),
    [userShops, districtId],
  );
  const activeShops = districtShops.filter((s) => s.shop_status === 'active');

  const perShopByShop = useMemo(() => {
    const m = new Map<string, PerShop>();
    for (const row of metricsQuery.data?.per_shop ?? []) {
      m.set(row.shop_id, row);
    }
    return m;
  }, [metricsQuery.data]);

  const rollup = useMemo(() => {
    let activeTickets = 0;
    let completed = 0;
    let techs = 0;
    let capacity = 0;
    for (const s of districtShops) {
      const row = perShopByShop.get(s.id);
      if (!row) continue;
      activeTickets += row.active_tickets;
      completed += row.completed_in_range;
      techs += row.total_techs;
      capacity += row.total_capacity;
    }
    return { activeTickets, completed, techs, capacity };
  }, [districtShops, perShopByShop]);

  const loading =
    isShopDataLoading ||
    scopeLoading ||
    districtQuery.isLoading ||
    orgQuery.isLoading ||
    regionQuery.isLoading ||
    metricsQuery.isLoading;

  const lookupError =
    (districtQuery.error ?? orgQuery.error ?? regionQuery.error ?? scopeError ?? null) as Error | null;

  // Render guards — see RegionDashboard for the ordering rationale.
  if (!districtId) {
    return <NotFoundCard label="District" />;
  }

  if (!loading && !district) {
    return <NotFoundCard label="District" />;
  }

  if (!loading && district && !hasAccess) {
    return (
      <DashboardShell>
        <Alert variant="destructive">
          <AlertTitle>Access denied</AlertTitle>
          <AlertDescription>
            You do not have access to this district. Contact an owner or
            organization admin if you believe this is unexpected.
          </AlertDescription>
        </Alert>
      </DashboardShell>
    );
  }

  if (lookupError) {
    return (
      <DashboardShell>
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load district data</AlertTitle>
          <AlertDescription>
            A required table returned an error. No changes have been made.
          </AlertDescription>
        </Alert>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      {/* Header — Stage 2 back navigation. Primary returns to the
          OptiNetwork hub; secondary keeps the legacy "OptiNetwork Map"
          affordance with its existing `floo-back-to-map` testid so the
          p7 §E doctrinal scan that asserts the map navigate target
          still resolves. */}
      <header className="flex items-center gap-2 mb-4 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/optinetwork')}
          className="gap-1 text-xs"
          data-testid="district-dashboard-back-to-opti-network"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to OptiNetwork
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/optinetwork/map')}
          className="gap-1 text-xs"
          data-testid="floo-back-to-map"
        >
          OptiNetwork Map
        </Button>
      </header>

      <div className="flex items-start gap-3 mb-2">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <Layers className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{district?.name ?? 'District'}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <Building2 className="h-3 w-3" />
            <span>{org?.name ?? 'Organization'}</span>
            {region && (
              <>
                <span>·</span>
                <MapIcon className="h-3 w-3" />
                {canOpenParentRegion ? (
                  <button
                    type="button"
                    className="underline hover:text-foreground"
                    onClick={() => navigate(`/org/regions/${region.id}`)}
                    data-testid="floo-district-open-parent-region"
                  >
                    {region.name}
                  </button>
                ) : (
                  <span>{region.name}</span>
                )}
              </>
            )}
            {district?.archived_at && (
              <Badge variant="outline" className="text-[10px]">Archived</Badge>
            )}
          </p>
        </div>
      </div>

      {district?.archived_at && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>District is archived</AlertTitle>
          <AlertDescription>
            This district has been archived. Its shops are still addressable,
            but new members and child entities cannot be assigned to it.
            Unarchive from the OptiNetwork Map to reactivate.
          </AlertDescription>
        </Alert>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Active shops"
          value={activeShops.length}
          icon={<Store className="h-4 w-4" />}
        />
        <KpiCard
          label="Total shops"
          value={districtShops.length}
        />
        <KpiCard
          label="Active tickets today"
          value={rollup.activeTickets}
        />
        <KpiCard
          label="Completed today"
          value={rollup.completed}
        />
      </div>

      {/* Shops table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Store className="h-4 w-4 text-muted-foreground" />
            Shops in this district
            <Badge variant="outline" className="text-[10px]">
              {districtShops.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {districtShops.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No shops assigned to this district yet. Attach shops via the
              OptiNetwork Map.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shop</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Active tickets</TableHead>
                  <TableHead className="text-right">Completed today</TableHead>
                  <TableHead className="text-right">Techs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {districtShops.map((s) => {
                  const r = perShopByShop.get(s.id);
                  const inactive = s.shop_status !== 'active';
                  return (
                    <TableRow key={s.id} data-testid={`floo-district-shop-row-${s.id}`}>
                      <TableCell
                        className={cn(
                          'font-medium',
                          inactive && 'line-through text-muted-foreground',
                        )}
                      >
                        {s.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant={inactive ? 'outline' : 'secondary'} className="text-[10px]">
                          {s.shop_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{r?.active_tickets ?? 0}</TableCell>
                      <TableCell className="text-right">{r?.completed_in_range ?? 0}</TableCell>
                      <TableCell className="text-right">{r?.total_techs ?? 0}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  );
};

function DashboardShell({ children }: { children: React.ReactNode }) {
  return <div className="container mx-auto py-6 px-4 max-w-5xl">{children}</div>;
}

function KpiCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          {icon}
          {label}
        </p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function NotFoundCard({ label }: { label: string }) {
  return (
    <DashboardShell>
      <Card>
        <CardContent className="py-10 text-center space-y-2">
          <Network className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="font-medium">{label} not found</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            The {label.toLowerCase()} you’re trying to open doesn’t exist or
            isn’t visible to your account. It may have been archived or
            removed.
          </p>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

function todayString(): string {
  return format(clock.now(), 'yyyy-MM-dd');
}

export default DistrictDashboard;
